// Provides configuration for each scene

export interface IOptions {
  text: string;
  time: number;
  happiness: number;
  battery?: number;
}

export interface IQuestion {
  text: string;
  options: IOptions[];
}

const enum CarType {
  NONE = "",
  DAY = "car-day-",
  NIGHT = "car-night-",
  PARKED = "car-parked-",
}
export interface IChallenge {
  imageUrl: string;
  carType: CarType;
  question: IQuestion;
  fact?: string;
  battery?: number;
}

export const challenges: IChallenge[] = [
  {
    imageUrl: "bg-game-home.jpg",
    carType: CarType.NONE,
    question: {
      text: "Your vehicle's battery is at 50%. You have a lot of miles to drive on your road trip and want to make some good headway on the first leg of the trip. Should you charge your vehicle the night before?",
      options: [
        { text: "Yes, charging at home is much more convenient and more manageable.", time: 0, happiness: 1, battery: 100},
        { text: "No, I don't have charging set up at my home.", time: 1, happiness: 0, battery: 50 },
        { text: "No, I might be able to find free charging while I'm in route.", time: 0, happiness: -1, battery: 50 },
      ],
    },
    fact: "According to Car and Driver, overall, home charging is typically much cheaper than paying for gasoline in a comparable vehicle—sometimes less than half the price.",
    battery: 50,
  },
  {
    imageUrl: "bg-boston-rain.jpg",
    carType: CarType.NIGHT,
    question: {
      text: "IT'S POURING RAIN. You can't even see out the front windshield. What do you do?",
      options: [
        { text: "Drive slowly and keep going.", time: 1, happiness: 0 },
        { text: "Pull over.", time: 2, happiness: 1 },
        { text: "Caution and road rules don't apply to me.", time: 0, happiness: -2 },
      ],
    },
  },
  {
    imageUrl: "bg-traffic-jam.jpg",
    carType: CarType.DAY,
    question: {
      text: "You encounter tons of traffic! The kids are getting restless. All the lanes are at a standstill. What do you do?",
      options: [
        {
          text: "Get off the highway and take the back roads. You need to find out if there are any chargers out there.",
          time: 1,
          happiness: 1,
        },
        { text: "Switch lanes and hope it's faster.", time: 1, happiness: -1 },
        { text: "Do nothing.", time: 1, happiness: -2 },
      ],
    },
    fact: "Sitting in traffic with navigation, the radio, and the air conditioning or heat running will reduce your EV's range. If possible, turn off all accessories. Some vehicles even have an Eco mode, which is designed to conserve your vehicle's energy or choose an alternate route.",
    battery: 35,
  },
  {
    imageUrl: "bg-highway-philly.jpg",
    carType: CarType.DAY,
    question: {
      text: "That traffic jam has caused your battery to get down to 18%, 25 miles before you reach your destination for the night. What should you do?",
      options: [
        { text: "Exit and hope there’s a vehicle charging station nearby.", time: 1, happiness: -1 },
        { text: "Drive to the hotel, we probably have enough charge to make it.", time: -1, happiness: -1 },
        { text: "Use an app to find a charging station nearby and reserve a place.", time: -1, happiness: 1 },
      ],
    },
    fact: "According to Car and Driver, the battery should be charged when it is between 20-80% to maintain better battery health. If you are on a federal alternative fuel corridor, you will see blue EV charging signs along the interstate. The directional signs will direct you to a charger located within a one-mile radius of the interstate. It is recommended that you join one or more charging networks and use their apps to locate a nearby charging station.",
    battery: 18,
  },
  {
    imageUrl: "bg-charge-stop-1.jpg",
    carType: CarType.PARKED,
    question: {
      text: "It's time to charge. You’ll need about 90 kWh to charge, and the charging station says it costs $0.30/kWh to charge. You have $25 left on the Visa gift card. Do you have enough to cover the entire charge?",
      options: [
        { text: "Sure do!", time: 1, happiness: -1 },
        { text: "Well, shoot. Nope.", time: -1, happiness: 1 },
      ],
    },
    fact: "As of April 2025, the national average cost to charge an electric vehicle (EV) at a public charging station in the United States is approximately $0.344 per kilowatt-hour (kWh), according to AAA.",
  },
  {
    imageUrl: "bg-charge-stop-1.jpg",
    carType: CarType.PARKED,
    question: {
      text: "A man approaches you and asks if your electric vehicle is doing more damage to the environment than a gas-powered car. What do you do?",
      options: [
        { text: "Inform him about EVs.", time: 0, happiness: 1 },
        { text: "Ignore and walk away.", time: 0, happiness: -1 },
      ],
    },
    fact: "Even EVs using electricity provided by 100% coal will use 31% less energy than gasoline-powered vehicles, and no state's electricity profile is 100% coal-powered.",
    battery: 100,
  },
  {
    imageUrl: "bg-night-philly.jpg",
    carType: CarType.NIGHT,
    question: {
      text: "It's time for a sleep break, but when you call to check on your reservation, the front desk worker can't find your reservation. Oh no! What are you going to do now?",
      options: [
        { text: "Argue with the hotel manager.", time: 0, happiness: -1 },
        { text: "Sleep in your car in the parking lot. It looks decently safe.", time: 0, happiness: -2 },
        {
          text: "Drive to the closest hotel. You are exhausted, so you’ll skip reading the hotel reviews.",
          time: 0,
          happiness: 1,
        },
      ],
    },
    battery: 65,
  },
  {
    imageUrl: "bg-highway-philly.jpg",
    carType: CarType.DAY,
    question: {
      text: "You wake up and get the family back in the car for another day of driving. As you're driving, you realize your range is lower than it usually is at full charge. You should have checked the weather. Today is super cold, meaning you will have a reduced range. You have to ask yourself, 'What should you do now'?",
      options: [
        { text: "Make more stops today and charge up more frequently.", time: 1, happiness: 1 },
        { text: "Pull off the highway and wait till tomorrow for warmer weather.", time: 2, happiness: -1 },
      ],
    },
    fact: "EVs can drop to a little more than half their expected range when temperatures are as low as 5 degrees Fahrenheit (-15C).",
    battery: 45,
  },
  {
    imageUrl: "bg-charge-stop-2.jpg",
    carType: CarType.PARKED,
    question: {
      text: "You pull off the highway, pick up the charger, and realize it is rated 50KW instead of 150KW. It's going to take quite a bit longer to get this car charged. What do you do?",
      options: [
        { text: "Wait even longer to charge the car.", time: 1, happiness: 0 },
        {
          text: "Keep driving and hope there is another charger on the route before you run out of range.",
          time: 0,
          happiness: -1,
        },
        { text: "Drive out of your way to go to a charger in town.", time: 2, happiness: -2 },
      ],
    },
    fact: "Level 3 charging stations have a power output range of 50 to 400 kW. Knowing the power rating of the station you will use and the fast-charging capability of your vehicle to accept a DCFC charge is essential to determining how long it will take to charge your vehicle.",
    battery: 15,
  },
  {
    imageUrl: "bg-charge-stop-2.jpg",
    carType: CarType.PARKED,
    question: {
      text: "You forgot to update your charging network app; the charging station you’ve come to does not have a plug compatible with your vehicle. What should you do?",
      options: [
        { text: "No big deal, I have an adapter.", time: 0, happiness: 1 },
        {
          text: "I guess I’ll get out my 50 ft extension cord and plug it into a random outlet somewhere.",
          time: 1,
          happiness: 0,
        },
        {
          text: "Update my charging network app to find a station nearby with a port compatible with my vehicle.",
          time: -1,
          happiness: 1,
        },
      ],
    },
    fact: "There are three adapter types that have been tested and validated: the Level 2 AC adapter for Level 2 J1772, the CCS1 to Tesla adapter, and the CHAdeMO to Tesla adapter. It is important to note that not all these adapters are compatible with all EVs, and it is good to know what adapter will work with your vehicle.  Take care when using a charging station adapter because, while they have undergone rigorous testing, they are more likely to experience safety issues.",
  },
  {
    imageUrl: "bg-new-mexico.jpg",
    carType: CarType.DAY,
    question: {
      text: "The kids are starving! They need to get something to eat. You can pull off at the next exit for food or wait for the next charging station. This decision could affect your reputation score. What do you do?",
      options: [
        { text: "Pull off now.", time: 1, happiness: 2 },
        { text: "Wait for the next charging station.", time: 0, happiness: -2 },
      ],
    },
    fact: "Driving habits that extend the mileage of a gasoline-powered vehicle can also extend the range of your EV. Be easy on the gas and brakes, and don't carry excess weight.",
    battery: 95,
  },
  {
    imageUrl: "bg-arizona-breakdown.jpg",
    carType: CarType.DAY,
    question: {
      text: "What's that thumping sound? It sounds like my tire. You pull off and see what's wrong with it. It’s a flat. What do you do?",
      options: [
        { text: "Put on the spare and keep driving", time: 1, happiness: 0 },
        { text: "Call AAA and have them tow your car and get it fixed", time: 2, happiness: -2 },
        { text: "Drive on the flat until you reach a gas station", time: 1, happiness: -1 },
      ],
    },
    battery: 90
  },
  {
    imageUrl: "bg-arizona-park.jpg",
    carType: CarType.DAY,
    question: {
      text: "You are passing an amusement park, and the kids look super excited! Do you stop?",
      options: [
        { text: "Yes", time: 2, happiness: 2 },
        { text: "No", time: 0, happiness: -1 },
      ],
    },
    battery: 80,
  },
  {
    imageUrl: "bg-charge-stop-3.jpg",
    carType: CarType.DAY,
    question: {
      text: "You are having a great time on your trip! You are 2,750 miles into the trip, and it's time to charge up! You pull off at the rest stop and go to the charging station. You notice someone parked their truck right in front of the charger, and now you can't charge. What are you going to do?",
      options: [
        { text: "Go inside the rest stop and search for the owner of the truck and get them to move it.", time: 0, happiness: -1 },
        { text: "Grab lunch at the rest stop and wait until they move their truck.", time: 1, happiness: 1 },
        { text: "Drive to a charger that is out of your way.", time: 2, happiness: -2 },
      ],
    },
    fact: "Most cables at EV charging stations are 16-18 feet long; it is possible that if you park adjacent to the charging station, the cord will still reach your vehicle's charging port.",
    battery: 30,
  },
  {
    imageUrl: "bg-charge-stop-3.jpg",
    carType: CarType.DAY,
    question: {
      text: "What is the best option when you don’t have enough funds available to cover charging your vehicle to 100% at a DC Fast charging station?",
      options: [
        { text: "Skip it altogether.", time: -1, happiness: -1 },
        { text: "Charge it to 80% or more, stopping when I’ve reached my dollar limit.", time: -1, happiness: 1, battery: 80 },
        { text: "Replenish funds in my account before fully charging to 100%.", time: 1, happiness: 1, battery: 100 },
      ],
    },
    fact: "Charging station networks offer smartphone apps that you can use to find an open station. Set up the smartphone app to automatically pay for charging, and you’ll pull in, connect, charge, and go without swiping a credit card or handing over cash to a clerk. You can also use a charging station app like PlugShare to find no-cost charging stations near you.",
  },
  {
    imageUrl: "bg-no-service.jpg",
    carType: CarType.DAY,
    question: {
      text: "You are getting close. Oh no, what happened to the blue line!? No service means no GPS. How long was it to stay on this highway? Are we close? What should you do?",
      options: [
        { text: "Nothing, hope it re-connects to the GPS.", time: 0, happiness: -1 },
        { text: "Pull over and ask for directions.", time: 1, happiness: 1 },
      ],
    },
  },
];
